// Budget-specific types for Access Project Management

import type { ProjectType, ProjectHealth } from './index';

// Re-export for convenience
export type { ProjectType, ProjectHealth };

// ============================================================================
// COMMON TYPES
// ============================================================================

export type BudgetStatus = 'green' | 'amber' | 'red';
export type TrendDirection = 'up' | 'down' | 'stable' | 'neutral';

// ============================================================================
// SUMMARY CARDS
// ============================================================================

export interface BudgetMetric {
  budget: number;
  actual: number;
  variance: number;
  variancePercent: number;
  progressPercent: number;
  trend: TrendDirection;
  trendValue?: string;
  status: BudgetStatus;
  remaining?: number;
  committed?: number;
  available?: number;
}

export interface MarginMetric {
  budgetedMargin: number;
  actualMargin: number;
  variance: number;
  status: BudgetStatus;
  trend?: TrendDirection;
}

export interface HoursMetric {
  budget: number;
  actual: number;
  remaining: number;
  progressPercent: number;
  status: BudgetStatus;
  trend?: TrendDirection;
  trendValue?: string;
}

export interface HealthMetric {
  score: ProjectHealth;
  statusText: string;
  factors: string[];
  message?: string;
}

export interface BudgetSummary {
  revenue: BudgetMetric;
  costs: BudgetMetric;
  margin: MarginMetric;
  hours: HoursMetric;
  health: HealthMetric;
}

// ============================================================================
// COST BUDGET
// ============================================================================

export type CostCategoryType = 
  | 'labour' 
  | 'expenses' 
  | 'subcontractors'
  | 'materials'
  | 'equipment'
  | 'overhead'
  | 'contingency';

// Canonical ordering and display labels for the system-seeded CategoryType set
// (see database Scripts/03_SeedData/019_CategoryType_SeedData.sql, ordered by DisplayOrder).
// Keep in sync with the seeded rows; the Record type forces a label for every CostCategoryType.
export const COST_CATEGORY_TYPE_ORDER: readonly CostCategoryType[] = [
  'labour',
  'expenses',
  'subcontractors',
  'materials',
  'equipment',
  'overhead',
  'contingency',
];

export const COST_CATEGORY_TYPE_LABELS: Record<CostCategoryType, string> = {
  labour: 'Labour',
  expenses: 'Expenses',
  subcontractors: 'Subcontractors',
  materials: 'Materials',
  equipment: 'Equipment',
  overhead: 'Overhead',
  contingency: 'Contingency',
};

export interface CostCategory {
  id: number;
  name: string;
  code?: string;
  type: CostCategoryType;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  progressPercent: number;
  subcategories?: CostSubcategory[];
  scope?: 'global' | 'project'; // Hybrid model: global (read-only) or project-specific (editable)
  globalCategoryId?: number; // Reference to global category if scope is 'global'
  isCustom?: boolean; // True for project-specific categories
  monthlyBudgets?: Record<string, number>; // Monthly budget breakdown: {"2024-12": 1000, "2025-01": 500, ...}
}

export interface CostSubcategory {
  id: number;
  
  name: string;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  progressPercent: number;
  scope?: 'global' | 'project';
  globalSubcategoryId?: number;
  isCustom?: boolean;
}

export interface PhaseBudget {
  id: number;
  
  phaseId: number;
  phaseName: string;
  name?: string;
  budget: number;
  actual: number;
  variance: number;
  status: 'not-started' | 'in-progress' | 'complete' | 'completed';
}

export interface CostBudgetTotals {
  budget: number;
  actual: number;
  committed: number;
  available: number;
  progressPercent: number;
}

/** Pending cost-centre budget entries collected during editing, persisted only on Save. */
export interface CostCentreBudgetPending {
  costCentreId: number;
  budgetType: 'cost' | 'sales' | 'resource';
  monthlyBudgets: Record<string, number>;
}

export interface CostBudget {
  projectId: number;
  categories: CostCategory[];
  phases: PhaseBudget[];
  totals: CostBudgetTotals;
  /** Per-cost-centre monthly budgets used by undo/redo in Cost Centre mode */
  costCentreBudgets?: Record<string, Record<string, number>>;
  /** Pending CC-level budgets — only present during editing, consumed by the save flow. */
  pendingCcBudgets?: CostCentreBudgetPending[];
}

// ============================================================================
// SALES BUDGET
// ============================================================================

export type SalesCategoryType = 
  | 'labour' 
  | 'expenses' 
  | 'subcontractors'
  | 'materials'
  | 'equipment'
  | 'overhead'
  | 'contingency';

export interface SalesCategory {
  id: number;
  name: string;
  code?: string;
  type: SalesCategoryType;
  budget: number;
  actual: number;
  remaining: number;
  percentBilled: number;
  scope?: 'global' | 'project'; // Hybrid model: global (read-only) or project-specific (editable)
  globalCategoryId?: number; // Reference to global category if scope is 'global'
  isCustom?: boolean; // True for project-specific categories
  monthlyBudgets?: Record<string, number>; // Monthly budget breakdown: {"2024-12": 1000, "2025-01": 500, ...}
}

// Extended category interface with monthly budgets as array for form display and storage
export interface CategoryBudgetRow extends Omit<SalesCategory, 'monthlyBudgets'> {
  monthlyBudgets: number[]; // Budget per month (array for form display, converted to/from Record for API)
  projectTotal?: number; // Total budget for the project
  actualTotal?: number; // Total actual spend
}

export type BillingMilestoneStatus = 
  | 'paid' 
  | 'invoiced'
  | 'due' 
  | 'pending' 
  | 'future' 
  | 'overdue';

/**
 * Project-level billing trigger type.
 * All milestones on a project share the same trigger type — it is set once on
 * the Project, not per-milestone.
 * - 'date'             — each milestone fires on its dueDate
 * - 'percent-complete' — each milestone fires when project.completionPercent >= triggerPercent
 */
export type BillingTriggerType = 'date' | 'percent-complete';

/** @deprecated Use BillingTriggerType instead. Kept for backwards compat during migration. */
export type BillingMilestoneTriggerType = BillingTriggerType;

export interface BillingMilestone {
  id: number;
  name: string;
  amount: number;
  dueDate: string;
  status: BillingMilestoneStatus;
  /**
   * The project completion threshold (0–100) at which this milestone becomes due.
   * Only relevant when the project's `billingTriggerType === 'percent-complete'`.
   * Optional deadline date is stored in `dueDate` for % complete milestones.
   */
  triggerPercent?: number;
}

export interface RevenueRecognition {
  method: 'percentage-of-completion' | 'milestone' | 'time-based';
  percentComplete: number;
  revenueToRecognise: number;
  revenueRecognised: number;
  deferredRevenue: number;
  unbilledRevenue: number;
  toRecognise?: number;
  recognised?: number;
  deferred?: number;
  unbilled?: number;
}

export interface RevenueStream {
  id: number;
  name: string;
  type: 'billable-time' | 'expense-recovery' | 'fixed-fee' | 'other';
  estimated: number;
  actual: number;
  invoiced: number;
  remaining: number;
  subcategories?: RevenueSubcategory[];
}

export interface RevenueSubcategory {
  id: number;
  name: string;
  estimated: number;
  actual: number;
  invoiced: number;
  remaining: number;
}

export interface RateCardEntry {
  id: number;
  role: string;
  billRate: number;
  estimatedHours: number;
  actualHours: number;
  revenue: number;
}

export interface RetainerMonth {
  month: string;
  retainerAmount: number;
  hoursIncluded: number;
  hoursUsed: number;
  overageHours: number;
  overageAmount: number;
  totalAmount: number;
}

export interface RetainerSummary {
  monthlyAmount: number;
  contractMonths: number;
  totalContract: number;
  contractValue?: number;
  invoiced: number;
  remaining: number;
  monthsCompleted?: number;
  monthsRemaining?: number;
  currentMonth: {
    hoursIncluded: number;
    hoursUsed: number;
    hoursRemaining: number;
    overageRate: number;
    progressPercent: number;
  };
  rolloverPolicy: 'none' | 'limited' | 'unlimited';
  rolloverLimit?: number;
  monthlyBreakdown: RetainerMonth[];
}

export interface SalesBudgetUtilisation {
  currentMonth: string;
  includedHours: number;
  hoursUsed: number;
  hoursRemaining: number;
  overageRate: number;
  percentUsed: number;
  rolloverPolicy: 'none' | 'limited' | 'unlimited';
  rolloverHours?: number;
}

export interface SalesBudgetMonthlyBreakdown {
  month: string;
  retainerAmount: number;
  hoursIncluded: number;
  hoursUsed: number;
  overageAmount: number;
  totalBilled: number;
}

export interface SalesBudgetPhase {
  id: number;
  name: string;
  allocated: number;
  recognised: number;
  invoiced: number;
  variance: number;
}

export interface SalesBudgetContractValue {
  total: number;
  invoiced: number;
  recognised: number;
  remaining: number;
}

export interface SalesBudgetEstimated {
  total: number;
  actual: number;
  invoiced: number;
  nte?: number;
}

export interface SalesBudget {
  projectId: number;
  projectType: ProjectType;
  
  // Fixed Price
  contractValue?: number;
  invoiced?: number;
  recognised?: number;
  remaining?: number;
  milestones?: BillingMilestone[];
  revenueRecognition?: RevenueRecognition;
  phases?: SalesBudgetPhase[];
  
  // Time & Materials
  estimated?: number;
  actual?: number;
  nteAmount?: number; // Not To Exceed
  streams?: RevenueStream[];
  rateCard?: RateCardEntry[]; // @deprecated - Hours tracking should be in Resource Budget only
  unbilledTime?: number;
  unbilledExpenses?: number;
  estimatedRevenue?: number;
  monthlyRetainer?: number;
  includedHours?: number;
  overageRate?: number;
  categories?: SalesCategory[]; // Sales budget categories with monthly breakdown
  totals?: {
    budget: number;
    actual: number;
    remaining: number;
    percentBilled: number;
  };
  
  // Retainer
  retainer?: RetainerSummary;
  utilisation?: SalesBudgetUtilisation;
  monthlyBreakdown?: SalesBudgetMonthlyBreakdown[];
  
  // Common
  phaseRevenue?: PhaseBudget[];

  /** Per-cost-centre monthly budgets used by undo/redo in Cost Centre mode */
  costCentreBudgets?: Record<string, Record<string, number>>;
  /** Pending CC-level budgets — only present during editing, consumed by the save flow. */
  pendingCcBudgets?: CostCentreBudgetPending[];
}

// ============================================================================
// RESOURCE BUDGET
// ============================================================================

export interface RoleBudget {
  id: number;
  roleId?: number;
  role: string;
  budgetHours: number;
  allocatedHours: number; // hours assigned to team members
  actualHours: number;
  remainingHours: number;
  percentUsed: number;
  billRate: number; // £/hour (sell rate)
  costRate?: number; // £/hour (cost rate from role definition)
  budgetCost: number; // budgetHours * costRate (falls back to billRate when costRate unavailable)
  actualCost: number; // actualHours * costRate
  monthlyHours?: Record<string, number>; // Keyed by 'YYYY-MM', e.g. {"2026-01": 100, "2026-02": 100}
  /** Signed delta between budgetHours and the previous approved revision (Phase 1.1) */
  varianceHours?: number;
  /** Forward-looking hours estimate from scheduling/forecast system (Phase 1.1) */
  forecastHours?: number;
  /**
   * Cost Centre mode only — the cost centre code this role entry belongs to.
   * Null in Category mode. The same roleId can appear multiple times with different
   * costCentreCode values (e.g. Senior Developer under CC-001 AND CC-002).
   */
  costCentreCode?: string | null;
}

export interface PersonBudget {
  id: number;
  personId: number;
  personName: string;
  role: string;
  budgetHours: number;
  actualHours: number;
  utilisation: number; // percentage
  costRate: number; // £/hour
  budgetCost: number; // budgetHours * costRate
  actualCost: number; // actualHours * costRate
}

export interface PhaseBudgetResource {
  phaseId: number;
  phaseName: string;
  budgetHours: number;
  actualHours: number;
  remainingHours: number;
  status: 'not-started' | 'in-progress' | 'complete';
  startDate?: string;
  endDate?: string;
}

export interface ResourceBudgetSummary {
  totalBudget: number; // total budget hours
  totalActual: number; // total actual hours
  totalRemaining: number; // total remaining hours
  progressPercent: number;
  billablePercent: number;
  targetBillablePercent: number;
  totalBudgetCost: number; // total budget cost
  totalActualCost: number; // total actual cost
}

export interface ResourceBudget {
  id?: number;
  projectId: number;
  summary: ResourceBudgetSummary;
  byRole: RoleBudget[];
  byPerson: PersonBudget[];
  byPhase: PhaseBudgetResource[];
  /** Pending CC-level budgets — only present during editing, consumed by the save flow. */
  pendingCcBudgets?: CostCentreBudgetPending[];
}

// ============================================================================
// ALL BUDGETS VIEW (PROFITABILITY)
// ============================================================================

export interface ProfitabilitySummary {
  revenue: {
    budget: number;
    actual: number;
    variance: number;
  };
  directCosts: {
    budget: number;
    actual: number;
    variance: number;
  };
  grossProfit: {
    budget: number;
    actual: number;
    variance: number;
  };
  grossMargin: {
    budget: number;
    actual: number;
    variance: number;
  };
  overhead: {
    budget: number;
    actual: number;
    variance: number;
  };
  netProfit: {
    budget: number;
    actual: number;
    variance: number;
  };
  netMargin: {
    budget: number;
    actual: number;
    variance: number;
  };
}

export interface BurnRateDataPoint {
  date: string;
  budgetCumulative: number;
  actualCumulative: number;
  forecastCumulative: number;
  period?: string;
  budgetPlan?: number;
  actualCost?: number;
}

export interface ForecastToComplete {
  projectedFinalRevenue: number;
  projectedFinalCost: number;
  projectedFinalMargin: number;
  revenueVariance: number;
  costVariance: number;
  marginVariance: number;
  warningMessage?: string;
  projectedRevenue?: number;
  projectedCost?: number;
  projectedMargin?: number;
  warnings?: string[];
}

export interface AllBudgetsView {
  projectId: number;
  profitability: ProfitabilitySummary;
  burnRateData: BurnRateDataPoint[];
  forecast: ForecastToComplete;
}

// ============================================================================
// BUDGET ALERTS
// ============================================================================

export type BudgetAlertType = 
  | 'cost-over-budget'
  | 'cost-warning'
  | 'revenue-behind'
  | 'margin-erosion'
  | 'hours-exceeded'
  | 'hours-warning'
  | 'billing-event-due'
  | 'billing-event-overdue';

export type BudgetAlertSeverity = 'critical' | 'warning' | 'info';

export interface BudgetAlert {
  id: number;
  projectId: number;
  type: BudgetAlertType;
  severity: BudgetAlertSeverity;
  title: string;
  description: string;
  message: string;
  details?: string;
  category?: string;
  actionLabel?: string;
  timestamp?: string;
  createdAt: string;
  dismissed: boolean;
}

// ============================================================================
// BUDGET REVISIONS
// ============================================================================

export type BudgetRevisionStatus = 'active' | 'draft' | 'historical' | 'pending-approval' | 'approved' | 'rejected' | 'current' | 'baseline';

// ============================================================================
// RESOURCE BUDGET REVISION — NEW TYPES (Phase 1.1)
// ============================================================================

/**
 * Reason category for a resource, cost, or sales budget revision.
 * 8 default PM-facing categories + 'custom' for org-defined additions.
 * 'resource-budget-cascade' is system-only — never shown in PM-facing dropdowns.
 */
export type RevisionReasonCategory =
  | 'scope-change'
  | 'client-request'
  | 'resource-substitution'
  | 'rate-change'
  | 'risk-mitigation'
  | 'estimation-correction'
  | 'phase-extension'
  | 'efficiency-gain'
  | 'resource-budget-cascade'   // System-only — auto-created when a Resource Budget cascade triggers a Cost/Sales update
  | 'custom';

/**
 * Role-level snapshot stored at the point a revision is saved.
 * Allows historical views to render the exact budget as it was.
 */
export interface RevisionRoleSnapshot {
  roleId: number;
  role: string;
  budgetHours: number;
  billRate: number;
  costRate?: number;
  budgetCost: number;
  /** Monthly hour breakdown keyed by 'YYYY-MM' — optional, only present when monthly tracking is enabled */
  monthlyHours?: Record<string, number>;
}

/**
 * Audit record of cascade decisions made at the point of saving a revision.
 * Stored per revision so the history is immutable.
 */
export interface RevisionCascadeRecord {
  /** Whether the Cost Budget's Labour lines were recalculated from this revision */
  costBudgetUpdated: boolean;
  /** Whether the Sales Budget's Labour lines were recalculated from this revision */
  salesBudgetUpdated: boolean;
  /** £ change applied to Cost Budget Labour (positive = increase) */
  costLabourChange?: number;
  /** £ change applied to Sales Budget Labour (positive = increase) */
  salesLabourChange?: number;
  /** True when the PM chose different cascade options from the org defaults */
  overriddenByUser: boolean;
  /** The GlobalCategory ID that the cascade targets */
  cascadeTargetCategoryId?: number;
  /** The name of the target category (for display/audit) */
  cascadeTargetCategoryName?: string;
}

// ============================================================================
// COST & SALES BUDGET REVISION — NEW TYPES (Phase 1.1)
// ============================================================================

/**
 * Category-level snapshot stored at the point a cost or sales budget revision is saved.
 * Allows historical views to render the exact category breakdown as it was.
 */
export interface RevisionCategorySnapshot {
  categoryId: number;
  categoryName: string;
  type: CostCategoryType;
  budget: number;
  /** Monthly budget breakdown at the time of this revision */
  monthlyBudget?: Record<string, number>;
}

/**
 * Per-category diff between two cost or sales budget revisions.
 */
export interface RevisionCategoryChange {
  categoryId: number;
  categoryName: string;
  /** Category type — used for grouping rows under Labour / Expenses / Subcontractors headers */
  type?: CostCategoryType;
  previousBudget: number;
  newBudget: number;
  /** Signed delta: positive = budget increased */
  budgetDelta: number;
  percentChange: number;
  changeType: 'increased' | 'decreased' | 'added' | 'removed' | 'unchanged';
}

/**
 * Per-category monthly value snapshot for a cost or sales budget revision.
 * Stored alongside `costSnapshot` / `salesSnapshot` when monthly tracking is enabled.
 */
export interface RevisionMonthlySnapshot {
  categoryId: number;
  categoryName: string;
  /** Key: 'YYYY-MM', Value: £ budget for that month */
  monthlyValues: Record<string, number>;
}

/**
 * The draft state of a revision.
 * - 'what-if'  — ephemeral scenario not yet committed as a real revision
 * - 'pending'  — saved draft awaiting approval or further editing
 * - null       — committed, approved revision (not a draft)
 */
export type RevisionDraftType = 'what-if' | 'pending' | null;

/**
 * Per-role diff between two revisions (or between a revision and its predecessor).
 */
export interface RevisionRoleChange {
  roleId: number;
  role: string;
  previousHours: number;
  newHours: number;
  /** Signed delta: positive = hours added, negative = hours reduced */
  hoursDelta: number;
  previousRate: number;
  newRate: number;
  /** Signed delta in £ cost */
  costDelta: number;
  changeType: 'added' | 'removed' | 'modified' | 'unchanged';
}

export interface BudgetRevisionBudgetData {
  totalCost?: number;
  totalRevenue?: number;
  margin?: number;
  costCategories?: Record<string, number>;
}

export interface BudgetRevisionChanges {
  totalCostChange: number;
  totalRevenueChange: number;
  categoriesChanged?: string[];
}

export interface BudgetRevision {
  id: number;
  revisionId?: string;
  projectId: number;
  revisionNumber: number;
  date: string;
  createdAt?: string;
  createdBy?: string;
  changedBy: string;
  changedByName: string;
  reason: string;
  status: BudgetRevisionStatus;
  
  // Budget values at this revision
  costBudget: number;
  salesBudget: number;
  resourceBudget: number;
  
  // Budget data snapshot
  budgetData?: BudgetRevisionBudgetData;
  
  // Changes from previous
  costChange?: number;
  salesChange?: number;
  resourceChange?: number;
  changes?: BudgetRevisionChanges;
  
  approvedBy?: string;
  approvedByName?: string;
  approvedDate?: string;
  approvedAt?: string;

  // --- Resource Budget Revision fields (Phase 1.1) ---

  /** Required category from the org-configured list */
  reasonCategory?: RevisionReasonCategory;
  /** Display label for the category (denormalised for history rendering) */
  reasonCategoryLabel?: string;
  /** Free-text detail expanding on the reason category */
  reasonDetail?: string;
  /** True when this revision has been starred as a named baseline */
  isBaseline?: boolean;
  /** Human-readable name for the baseline, e.g. "Contract Baseline" */
  baselineName?: string;
  /** Role-level snapshot of the resource budget at the time this revision was saved */
  resourceSnapshot?: RevisionRoleSnapshot[];
  /** Total budget hours across all roles at the time of this revision */
  totalHoursAtRevision?: number;
  /** Signed hour delta vs the immediately preceding revision */
  hoursChange?: number;
  /** Per-role diff vs the immediately preceding revision */
  roleChanges?: RevisionRoleChange[];
  /** Cascade decisions recorded at save time */
  cascadeRecord?: RevisionCascadeRecord;
  /** Draft type — null / undefined means this is a committed revision */
  draftType?: RevisionDraftType;
  /** User-supplied name for what-if or pending drafts */
  draftName?: string;

  // --- Cost & Sales Budget Revision fields (Phase 1.1) ---

  /**
   * Which budget layer this revision belongs to.
   * 'resource' for resource budget revisions (existing behaviour).
   * 'cost' / 'sales' for cost and sales budget revisions (new).
   * Defaults to 'resource' when absent for backwards compatibility.
   */
  budgetLayer?: 'resource' | 'cost' | 'sales';
  /** Category-level snapshot of the cost budget at the time this revision was saved */
  costSnapshot?: RevisionCategorySnapshot[];
  /** Category-level snapshot of the sales budget at the time this revision was saved */
  salesSnapshot?: RevisionCategorySnapshot[];
  /** Per-category monthly value snapshot (present when monthly tracking is enabled) */
  monthlySnapshot?: RevisionMonthlySnapshot[];
  /** Per-category diff vs the immediately preceding revision */
  categoryChanges?: RevisionCategoryChange[];
  /** Total £ budget across all categories at the time of this revision */
  totalBudgetAtRevision?: number;
  /** Signed £ delta vs the immediately preceding revision */
  budgetChange?: number;
  /**
   * ID of the Resource Budget revision that triggered this cost/sales revision.
   * Only set when `cascadeSource === true`.
   */
  linkedResourceRevisionId?: number;
  /**
   * True when this revision was auto-created by a Resource Budget cascade
   * (i.e. `reasonCategory === 'resource-budget-cascade'`).
   */
  cascadeSource?: boolean;
}

export interface BudgetComparison {
  fromRevision: BudgetRevision;
  toRevision: BudgetRevision;
  changes: {
    category: string;
    fromValue: number;
    toValue: number;
    change: number;
    changePercent: number;
  }[];
}

// ============================================================================
// BUDGET EDIT
// ============================================================================

export interface BudgetEditFormData {
  costCategories: {
    labour: {
      consulting: number;
      development: number;
      design: number;
    };
    expenses: {
      travel: number;
      software: number;
      other: number;
    };
    subcontractors: number;
  };
  salesBudget: {
    contractValue?: number;
    milestones?: BillingMilestone[];
    estimated?: number;
    nteAmount?: number;
  };
  resourceBudget: {
    byRole: {
      role: string;
      hours: number;
      rate: number;
    }[];
  };
  revisionReason: string;
  saveAsBaseline: boolean;
}

// ============================================================================
// COMPLETE BUDGET DATA
// ============================================================================

export interface ProjectBudgetData {
  projectId: number;
  projectName: string;
  projectType: ProjectType;
  summary: BudgetSummary;
  costBudget: CostBudget;
  salesBudget: SalesBudget;
  resourceBudget: ResourceBudget;
  allBudgets: AllBudgetsView;
  alerts: BudgetAlert[];
  revisions: BudgetRevision[];
  lastUpdated: string;
}

// Alias for backward compatibility
export type BudgetData = ProjectBudgetData;

// ============================================================================
// BUDGET VARIANCE REPORT TYPES
// ============================================================================

/**
 * A single subcategory row in the Budget Variance Report cost section.
 * Mirrors the endpoint response shape from Section 8.1 of the Budget Variance PRD.
 */
export interface BudgetVarianceSubcategory {
  id: number;
  name: string;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  variance: number;
  variancePercent: number;
  progressPercent: number;
  status: BudgetStatus;
}

/**
 * A single category row in the Budget Variance Report cost section.
 * Carries optional subcategories for drill-down expansion.
 *
 * When a historical revision is selected as baseline:
 * - `budget` reflects the value from `BudgetRevision.costSnapshot` for this category
 * - `actual`, `committed`, `available` are always live values
 * - `status` may be `'removed'` for categories that existed in the snapshot but have since been deleted
 */
export interface BudgetVarianceCategory {
  id: number;
  name: string;
  type: CostCategoryType;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  variance: number;
  variancePercent: number;
  progressPercent: number;
  /** Standard RAG health status. Extended with `'removed'` for categories deleted since a historical revision. */
  status: BudgetStatus | 'removed';
  subcategories?: BudgetVarianceSubcategory[];
}

/**
 * Aggregated cost totals row for the Budget Variance Report.
 * Always sourced from `CostBudgetTotals` — never derived from summing category fields.
 */
export interface BudgetVarianceTotals {
  budget: number;
  actual: number;
  committed: number;
  available: number;
  variance: number;
  variancePercent: number;
}

/**
 * Revenue section of the Budget Variance Report.
 * `budget` = `project.budgetRevenue` (Sales Budget contract value).
 * `actual` = `project.actualSales`.
 */
export interface BudgetVarianceRevenue {
  budget: number;
  actual: number;
  variance: number;
  variancePercent: number;
}

/**
 * Hours section of the Budget Variance Report.
 * Not financial data — values must NOT be wrapped in `<SensitiveData>`.
 */
export interface BudgetVarianceHours {
  budget: number;
  actual: number;
  variance: number;
  variancePercent: number;
}

/**
 * Describes the revision currently used as the cost budget baseline.
 * When `revisionId` is absent or `isActiveRevision` is true, the live active
 * revision is the baseline.
 */
export interface BudgetVarianceBaseline {
  revisionId?: number;
  revisionLabel: string;
  /** True when this baseline is the currently active (approved) revision */
  isActiveRevision: boolean;
  /** True when this revision was explicitly marked as the original contract baseline */
  isOriginalBaseline?: boolean;
  date?: string;
}

/**
 * A revision option shown in the Baseline Revision selector dropdown.
 */
export interface BudgetVarianceRevisionOption {
  id: number;
  label: string;
  status: BudgetRevisionStatus;
  /** True when this is the active (live) revision */
  isActive: boolean;
  /** True when this revision is the original baseline */
  isBaseline: boolean;
  date: string;
}

/**
 * Top-level shape of the Budget Variance Report data.
 * Matches the endpoint response defined in Section 8.1 of the Budget Variance PRD.
 *
 * All category-level financial figures under `costs` must be wrapped in
 * `<SensitiveData>` in the UI — actuals, budgets, committed, available, and
 * variance values are sensitive.
 */
export interface BudgetVarianceReport {
  projectId: number;
  projectName: string;
  projectCode: string;
  baseline: BudgetVarianceBaseline;
  revenue: BudgetVarianceRevenue;
  costs: {
    totals: BudgetVarianceTotals;
    categories: BudgetVarianceCategory[];
  };
  hours: BudgetVarianceHours;
  /** All non-draft cost revisions for the project — used to populate the Baseline selector */
  revisions: BudgetVarianceRevisionOption[];
}
